import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../StudentAuthContext';
import Loading from '../components/loading';

const StudentLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const { studentLogin, currentStudent } = useStudentAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentStudent) {
      console.log(currentStudent);
      navigate('/student-dashboard');
    }
  }, [currentStudent, navigate]);
  const handleStaffLoginNavigation = () => {
    navigate('/');
  };
  
  const handleSubAgentLoginNavigation = () => {
    navigate('/');
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleLogin = async () => {
    setError('');
    setUsernameError(false);
    setPasswordError(false);

    if (!username) {
      setUsernameError(true);
    }
    if (!password) {
      setPasswordError(true);
    }
    if (!username || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setIsLoading(true);

    try {
      await studentLogin(username, password);
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = 'Login failed. Please check your credentials.';
      
      // Handle specific error messages from the Cloud Function
      if (err?.message) {
        switch (err.message) {
          case 'INVALID_USERNAME_FORMAT':
            errorMessage = 'Invalid username format. Please use format: ID@company';
            break;
          case 'COMPANY_NOT_FOUND':
            errorMessage = 'Company not found. Please check your username.';
            break;
          case 'INVALID_CREDENTIALS':
            errorMessage = 'Invalid username or password.';
            break;
          default:
            errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="h-screen flex flex-col md:flex-row">
      <div
        className="hidden md:block w-3/4 flex flex-col justify-between p-4 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/images/desktop/bg-login.png')`,
        }}
      >
        <div className="absolute top-7 left-12">
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/login-main-logo1.svg`}
            alt="Student Portal Logo"
            className="h-16"
          />
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/bg-student.png`}
            alt="Student Portal Graphic"
            style={{ maxHeight: '75vh', minHeight: '65vh' }}
          />
        </div>
        <div className="absolute bottom-20 left-14 text-slate-900 font-bold text-l w-[172px]">
        Efficiency Redefined: Empowering Teams, Enhancing Conversions with{" "}
        <span className="text-blue-500">ScholarCRM</span>
        </div>
      </div>

      <div className="w-full md:w-1/4 bg-white flex flex-col justify-center items-center px-4 py-10">
        <div className="h-[90px] flex items-center justify-center mb-10">
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/login-main-logo.svg`}
            alt="Student Portal Logo"
            className="h-35 w-full object-contain"
          />
        </div>
        <h2 className="font-[Montserrat] text-[16px] font-medium text-left w-full mb-1">Student Sign In</h2>
        <p className="text-xs text-gray-400 font-[Montserrat] text-left w-full mb-3">
          Enter your ID and password to access your account.
        </p>
        <div className="w-full mb-3">
          <label htmlFor="username" className="text-xs font-semibold text-gray-700">
            Student ID
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter your ID "
            className={`w-full p-2 mt-1 text-sm border ${usernameError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${usernameError ? 'focus:ring-red-500' : 'focus:ring-blue-600'}`}
          />
          {usernameError && <p className="text-red-500 text-xs mt-1">Student ID is required.</p>}
        </div>
        <div className="w-full mb-3 relative">
          <label htmlFor="password" className="text-xs font-semibold text-gray-700">
            Password
          </label>
          <input
            type={passwordVisible ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter your password"
            className={`w-full p-2 mt-1 text-sm border ${passwordError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${passwordError ? 'focus:ring-red-500' : 'focus:ring-blue-600'} pr-10`}
          />
          <div className="absolute top-[74%] transform -translate-y-1/2 right-3 cursor-pointer" onClick={togglePasswordVisibility}>
            <img
              src={passwordVisible ? `${process.env.PUBLIC_URL}/images/desktop/eye-open.svg` : `${process.env.PUBLIC_URL}/images/desktop/eye-close.svg`}
              alt="Toggle Password Visibility"
              className="w-6 h-6"
            />
          </div>
          {passwordError && <p className="text-red-500 text-xs mt-1">Password is required.</p>}
        </div>
        <button
          onClick={handleLogin}
          className="w-full py-2 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-700"
        >
          Log In
        </button>
        {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
        <p className="font-Montserrat text-blue-500 text-xs cursor-pointer mt-2 hover:underline"     
            onClick={handleStaffLoginNavigation}>Staff Login</p>
        <p className="font-Montserrat text-blue-500 text-xs cursor-pointer mt-2 hover:underline"     
            onClick={ handleSubAgentLoginNavigation}>Sub Agent Login</p>
      </div>
    </div>
  );
};

export default StudentLogin;